<template>
  <div class="div-home">
    <div class="div-info-user">
      <span class="name-user">
        {{ `${$t('Home.Hello')}, ${userData.first_name}` }}
      </span>

      <div class="custom-carousel">
        <transition
          name="text-carousel-fade"
          mode="out-in"
        >
          <div
            :key="activePhrase"
            class="carousel-text"
          >
            {{ $t(activePhrase) }}
          </div>
        </transition>

        <div class="text-carousel-indicators">
          <button
            v-for="(phrase, index) in phrases"
            :key="phrase"
            type="button"
            class="indicator"
            :class="{ active: index === activePhraseIndex }"
            aria-label="Trocar frase"
            @click="setPhrase(index)"
          />
        </div>
      </div>
    </div>

    <div
      v-if="products.length === 0"
      class="no-access"
    >
      <div class="title">{{ $t('Home.TitleNoAccess') }}</div>
      <div class="subtitle">{{ $t('Home.SubtitleNoAccess') }}</div>
    </div>

    <div v-else>
      <div
        v-if="products.length <= 6"
        class="div-products"
      >
        <card-product
          v-for="(item, index) in products"
          :key="index"
          :data="item"
        >
        </card-product>
      </div>

      <div
        v-else
        class="div-products-carousel"
      >
        <b-carousel
          id="carousel-products"
          :interval="0"
          indicators
          controls
          class="products-carousel"
        >
          <b-carousel-slide
            v-for="(chunk, idx) in chunkedProducts"
            :key="idx"
          >
            <template #img>
              <div class="products-slide">
                <card-product
                  v-for="(item, index) in chunk"
                  :key="index"
                  :data="item"
                />
              </div>
            </template>
          </b-carousel-slide>
        </b-carousel>
      </div>
    </div>
  </div>
</template>

<script>
  import CardProduct from './components/CardProduct.vue';
  import { mapState } from 'vuex';

  export default {
    name: 'HomeView',
    components: {
      CardProduct
    },
    data() {
      return {
        products: [],
        phrases: ['Home.RotatingPhrases1', 'Home.RotatingPhrases2', 'Home.RotatingPhrases3'],
        activePhraseIndex: 0,
        phraseIntervalId: null
      };
    },
    beforeMount() {
      const userInfo = this.$cookies.get('userInfo');
      if (userInfo?.passwordChangeRequired) {
        this.redirect('first-access-reset-password');
      } else if (userInfo?.pinChangeRequired) {
        this.redirect('first-access-reset-pin');
      }
    },
    mounted() {
      this.verifyPermissions();
      this.startPhraseRotation();
    },
    beforeDestroy() {
      this.stopPhraseRotation();
    },
    methods: {
      redirect(page) {
        if (this.$route.name === page) return;
        this.$router.push({ name: page }).catch(() => {});
      },
      verifyPermissions() {
        let modulesPermissions = [];

        if (!this.permissions || !Array.isArray(this.permissions)) {
          this.products = [];
          return;
        }
        this.permissions.map((e) => {
          switch (true) {
            case e.name === 'mysfc' &&
              !!e?.children?.some((children) => !!children?.options?.length):
              modulesPermissions.push({
                name: 'MySFC',
                info: 'Home.ExplanatoryTextMySfc',
                redirect: this.$mySfcLink
              });
              break;
            // caso exista páginas com permissões no connect mudar para e.children
            case e.name === 'myconnect' && e.options.length > 0:
              modulesPermissions.push({
                name: 'MyConnect',
                info: 'Home.ExplanatoryTextMyConnect',
                redirect: this.$myConnectLink
              });
              break;
            case e.name === 'myconfig' &&
              !!e?.children?.some((children) => !!children?.options?.length):
              modulesPermissions.push({
                name: 'MyConfig',
                info: 'Home.ExplanatoryTextMyConfig',
                redirect: this.$myConfigLink
              });
              break;
            case e.name === 'mydashboard':
              modulesPermissions.push({
                name: 'MyDashboard',
                info: 'Home.ExplanatoryTextMyDashboard',
                redirect: this.$myDashboardLink
              });
              break;
            case e.name === 'myplan':
              modulesPermissions.push({
                name: 'MyPlan',
                info: 'Home.ExplanatoryTextMyPlan',
                redirect: this.$myPlanLink
              });
              break;
            case e.name === 'myforecast':
              modulesPermissions.push({
                name: 'MyForecast',
                info: 'Home.ExplanatoryTextMyForecast',
                redirect: this.$myForecastLink
              });
              break;
            case e.name === 'myquality':
              modulesPermissions.push({
                name: 'MyQuality',
                info: 'Home.ExplanatoryTextMyQuality',
                redirect: this.$myQualityLink
              });
              break;
            case e.name === 'mynotification':
              modulesPermissions.push({
                name: 'MyNotification',
                info: 'Home.ExplanatoryTextMyNotification',
                redirect: this.$myNotificationLink
              });
              break;
            case e.name === 'mycolab':
              modulesPermissions.push({
                name: 'MyColab',
                info: 'Home.ExplanatoryTextMyColab',
                redirect: this.$myColabLink
              });
              break;
            default:
              break;
          }
        });
        this.products = modulesPermissions;
      },
      startPhraseRotation() {
        if (this.phrases.length <= 1) return;
        this.stopPhraseRotation();
        this.phraseIntervalId = setInterval(() => {
          this.activePhraseIndex = (this.activePhraseIndex + 1) % this.phrases.length;
        }, 5000);
      },
      stopPhraseRotation() {
        if (!this.phraseIntervalId) return;
        clearInterval(this.phraseIntervalId);
        this.phraseIntervalId = null;
      },
      setPhrase(index) {
        if (index === this.activePhraseIndex) return;
        this.activePhraseIndex = index;
        this.startPhraseRotation();
      }
    },
    computed: {
      ...mapState('appStore', {
        permissions: 'permissions'
      }),
      userData() {
        const storeUser = this.$store?.state?.auth?.userInfo?.userData;
        if (storeUser && storeUser.first_name) return storeUser;
        try {
          return JSON.parse(localStorage.getItem('userData')) || {};
        } catch {
          return {};
        }
      },
      activePhrase() {
        return this.phrases[this.activePhraseIndex];
      },
      chunkedProducts() {
        const perSlide = 6;
        const chunks = [];
        for (let i = 0; i < this.products.length; i += perSlide) {
          chunks.push(this.products.slice(i, i + perSlide));
        }
        return chunks;
      }
    },
    watch: {
      permissions() {
        this.verifyPermissions();
      }
    }
  };
</script>
<style lang="scss">
  .text-carousel-fade-enter-active,
  .text-carousel-fade-leave-active {
    transition: opacity 0.4s ease, transform 0.4s ease;
  }

  .text-carousel-fade-enter-from,
  .text-carousel-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }

  .div-home {
    display: flex;
    flex-direction: column;
    min-height: calc(100dvh - 115px);
    gap: 24px;

    .div-info-user {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: start;
      justify-content: center;
      padding: 24px 29px 0px 29px;
      gap: 16px;

      .name-user {
        font-size: 22px;
        font-weight: 600;
        line-height: 30px;
        color: #fff;
      }

      .greeting {
        font-size: 28px;
        font-weight: 600;
        line-height: 38px;
        color: #fff;
      }

      .custom-carousel {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .text-carousel-indicators {
          display: flex;
          gap: 8px;

          .indicator {
            width: 30px;
            height: 3px;
            border-radius: 5px;
            background-color: #7e7570;
            border: none;
            padding: 0;
            cursor: pointer;
            transition: background-color 0.3s ease;

            &.active {
              background-color: #ffffff;
            }

            &:hover {
              background-color: #974900;
            }
          }
        }
      }

      .carousel-text {
        text-align: start;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        color: #cfc4be;
      }
    }

    .div-products {
      width: 100%;
      flex-wrap: wrap;
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      padding: 0px 29px;
      gap: 24px 30px;
    }

    .div-products-carousel {
      width: 100%;
      padding: 0px 29px;
      display: flex;
      align-items: center;
      gap: 24px;

      .products-slide {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        gap: 24px 30px;
        min-height: 625px;
        height: 625px;
        justify-content: flex-start;
        align-items: stretch;
        width: 100%;
        padding: 0 0 8px 0;
      }

      .products-carousel {
        display: flex;
        flex-direction: column;
        gap: 16px;
        flex: 1;

        .carousel-inner {
          position: relative;
        }

        .carousel-control-prev,
        .carousel-control-next {
          position: absolute;
          width: 48px;
          height: 48px;
          border: 1px solid #4c4541;
          background: rgba(26, 28, 25, 0.2);
          backdrop-filter: blur(25px);
          border-radius: 50%;
          opacity: 1;
          transition: all 0.3s;
          top: 32vh;
          // transform: translateY(-50%);

          &:hover {
            border: 1px solid #974900;
            background: rgba(151, 73, 0, 0.05);
            backdrop-filter: blur(25px);
          }

          .carousel-control-prev-icon,
          .carousel-control-next-icon {
            width: 24px;
            height: 24px;
            background-image: none;
          }

          .carousel-control-prev-icon::before,
          .carousel-control-next-icon::before {
            content: '';
            display: block;
            width: 100%;
            height: 100%;
            background-color: #974900;
            mask-size: contain;
            mask-repeat: no-repeat;
            mask-position: center;
          }
        }

        .carousel-control-prev-icon::before {
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
        }

        .carousel-control-next-icon::before {
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
        }

        .carousel-control-prev {
          left: -72px;
        }

        .carousel-control-next {
          right: -72px;
        }

        .carousel-indicators {
          position: absolute !important;
          bottom: 1vh !important;
          top: 650px !important;
          justify-content: flex-start !important;
          margin-left: 0 !important;
          margin-bottom: 0 !important;

          li {
            margin-right: 8px;
            margin-left: 0px;
            border: none;
            border-radius: 5px;
            background-color: #7e7570;

            &:hover {
              opacity: 1;
              background-color: #974900;
            }

            &.active {
              background-color: #ffffff;
            }
          }
        }
      }
    }

    .no-access {
      display: flex;
      flex-direction: column;
      padding: 24px;
      margin: 0 24px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 24px;
      text-decoration: none;
      border: 1px solid #4c4541;
      background: rgba(26, 28, 25, 0.2);
      backdrop-filter: blur(25px);
      transition: background-color 0.5s;
      height: calc(100vh - 274px);

      .title {
        color: #fff;
        font-size: 18px;
        font-weight: 600;
        line-height: 26px;
      }

      .subtitle {
        color: #fff;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
      }

      @media (max-width: 480px) {
        .title {
          font-size: 14px;
          line-height: 20px;
        }

        .subtitle {
          font-size: 12px;
          line-height: 16px;
        }
      }
    }
  }

  @media screen and (max-width: 1215px) {
    .div-home {
      .div-products {
        flex-wrap: wrap;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .div-home {
      .carousel-text {
        height: 44px;
      }

      .div-products {
        gap: 24px 16px;
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .div-home {
      .div-products-carousel {
        padding: 0px 16px;

        .products-carousel {
          .carousel-control-prev {
            left: -56px;
          }

          .carousel-control-next {
            right: -56px;
          }
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .div-home {
      .div-products-carousel {
        .products-carousel {
          .carousel-control-prev,
          .carousel-control-next {
            width: 40px;
            height: 40px;
          }

          .carousel-control-prev {
            left: -48px;
          }

          .carousel-control-next {
            right: -48px;
          }
        }
      }
    }
  }

  @media screen and (max-width: 425px) {
    .div-home {
      .div-info-user {
        padding: 24px 16px 0px 16px;

        .name-user {
          font-size: 14px;
          line-height: 20px;
        }

        .custom-carousel {
          gap: 16px;
        }

        .carousel-text {
          font-size: 12px;
          line-height: 16px;
          height: 32px;
        }
      }

      .div-products {
        padding: 0px 16px 24px 16px;
      }

      .div-products-carousel {
        padding: 0px 8px;

        .products-carousel {
          .carousel-control-prev,
          .carousel-control-next {
            width: 32px;
            height: 32px;

            .carousel-control-prev-icon,
            .carousel-control-next-icon {
              width: 16px;
              height: 16px;
            }
          }

          .carousel-control-prev {
            left: -40px;
          }

          .carousel-control-next {
            right: -40px;
          }
        }
      }
    }
  }
</style>
